import React, { useLayoutEffect, useRef, useState } from "react";
import "./ProductCard.css";
import { productHasProteinBreadChoice, PROTEIN_BREAD_BADGE_SRC } from "../utils/productBadges";

// Helper function to decode HTML entities and strip HTML tags
const decodeHTMLEntities = (text) => {
  if (!text) return "";

  // Create a temporary element to decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  const decoded = textarea.value;

  // Remove any HTML tags
  const cleaned = decoded.replace(/<[^>]+>/g, "");

  return cleaned;
};

const getSingleVariant = (product) => {
  const variationGroups = Array.isArray(product?.variations) ? product.variations : [];
  const variantGroup = variationGroups.find(g => g?.isVariantGroup);
  if (variantGroup && (variantGroup.options?.length || 0) === 1) {
    return variantGroup.options[0];
  }
  return null;
};

export default function ProductCard({
  product,
  tags,
  onClick,
  onAddToCart,
  onDecreaseFromCart,
  cartQuantity = 0,
  cartLineCount = 0,
  highlightText,
  searchTerm,
  isSoldOut = false,
  isCartEnabled = false,
  variant = "row", // "row" (default list layout) | "tile" (kiosk photo-forward)
}) {
  const [showFullDetails, setShowFullDetails] = useState(false);
  const descRef = useRef(null);

  const cleanDescription = decodeHTMLEntities(product.description);

  // Phone tiles: the description fills whatever room the card has between the
  // name and the price (the card height is set by the photo, never by the text),
  // so show as many lines as actually fit instead of a fixed 2-line clamp.
  useLayoutEffect(() => {
    const el = descRef.current;
    if (variant !== "tile" || !el) return undefined;
    const phone = window.matchMedia("(max-width: 560px)");
    const fit = () => {
      el.style.removeProperty("max-height");
      if (!phone.matches) {
        el.style.removeProperty("-webkit-line-clamp");
        return;
      }
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      if (!lineHeight || !el.clientHeight) return;
      const lines = Math.max(1, Math.floor(el.clientHeight / lineHeight));
      el.style.setProperty("-webkit-line-clamp", String(lines));
      // Cap the box at whole lines, or a clipped part of the next line peeks out below the ellipsis.
      el.style.maxHeight = `${lines * lineHeight}px`;
    };
    fit();
    // The card (not the text) changes size with the screen; the text is height-capped by design.
    const card = el.closest(".product-card");
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(fit) : null;
    observer?.observe(card || el);
    phone.addEventListener?.("change", fit);
    return () => {
      observer?.disconnect();
      phone.removeEventListener?.("change", fit);
    };
  }, [variant, cleanDescription]);

  const toggleDetails = () => setShowFullDetails((prev) => !prev);

  const fallbackImage = "https://placehold.co/400x400?text=No+Image&font=roboto";
  const imageSrc =
    product.imageUrl || product.productDefaultImage || fallbackImage;

  const hasConfigurableOptions = (() => {
    const variationGroups = Array.isArray(product?.variations) ? product.variations : [];
    const variantGroup = variationGroups.find(g => g?.isVariantGroup);
    if (variantGroup && (variantGroup.options?.length || 0) > 1) return true;
    const nonVariantGroups = variationGroups.filter(g => !g?.isVariantGroup);
    if (nonVariantGroups.some(g => (g?.options?.length || 0) > 0)) return true;
    if (variantGroup) {
      const hasNestedOptions = (variantGroup.options || []).some(
        opt => opt?.optionSets?.some(os => (os?.options?.length || 0) > 0)
      );
      if (hasNestedOptions) return true;
    }
    return false;
  })();

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    if (isSoldOut || !onAddToCart) return;
    if (hasConfigurableOptions) {
      onClick(product, { source: 'add' });
      return;
    }
    const singleVariant = getSingleVariant(product);
    onAddToCart(product, singleVariant);
  };

  const handleQuickDecrease = (e) => {
    e.stopPropagation();
    if (isSoldOut || !onDecreaseFromCart) return;
    if (hasConfigurableOptions && cartLineCount > 1) {
      onClick(product, { source: 'customization' });
      return;
    }
    onDecreaseFromCart(product);
  };

  const handleCardClick = () => {
    onClick(product);
  };

  const combinedText = cleanDescription;

  // ---- Shared building blocks (reused by both row and tile variants) ----

  // Dietary / status tag icons (veg, egg, non-veg, new, protein bread…)
  const renderTags = () => {
    const productTags = product.tags || [];

    const hasEggTag = productTags.some(tag =>
      tag?.name?.toLowerCase().includes('egg')
    );
    const hasNonVegTag = productTags.some(tag => {
      const name = tag?.name?.toLowerCase() || '';
      return name.includes('non') && name.includes('veg');
    });
    const hasNewTag = productTags.some(tag => {
      const name = tag?.name?.toLowerCase() || '';
      return name.includes('new');
    });

    const hasEggAndNonVeg = hasEggTag && hasNonVegTag;
    const hasProteinBreadChoice = productHasProteinBreadChoice(product);

    // Show the custom egg icon whenever the product has an egg tag.
    if (hasEggTag || hasNewTag || hasProteinBreadChoice) {
      return (
        <div className="grouped-tags">
          {hasEggTag ? (
            <div
              className="egg-nonveg-container"
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              <img
                key="egg-tag"
                src="/EggTag.png"
                alt="Egg (Non-Veg)"
                className="egg-nonveg-icon"
              />
            </div>
          ) : ''}
          {productTags.map((tag, index) => {
            if (hasEggTag && tag?.name?.toLowerCase() === 'egg') {
              return null;
            }
            if (hasEggAndNonVeg && tag?.name?.toLowerCase().includes('non')) {
              return null;
            }
            else if (tag?.id === '6932636dec4624b7afca0266') {
              return (
                <div
                  key={tag.id}
                  className="egg-nonveg-container"
                  style={{ display: "flex", gap: 0, alignItems: "center" }}
                >
                  <img
                    src="/NewTag.png"
                    alt="Newly Launched"
                    className="egg-nonveg-icon"
                  />
                </div>
              );
            }
            else if (tag?.tagIcon) {
              return (
                <div
                  key={tag.id || index}
                  className="tag-icon-container"
                  style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 0 }}
                >
                  <img
                    src={tag?.tagIcon?.fullPath}
                    alt={tag?.name}
                    className="veg-icon"
                  />
                </div>
              );
            }
            return null;
          })}
          {hasProteinBreadChoice && (
            <div
              className="protein-tag-container"
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              <img
                src={PROTEIN_BREAD_BADGE_SRC}
                alt="Protein Bread"
                className="egg-nonveg-icon"
              />
            </div>
          )}
        </div>
      );
    }

    // Show tags normally
    return (
      <div
        className="grouped-tags"
        style={{ display: "flex", gap: 6, alignItems: "center" }}
      >
        {productTags.map((tag, index) => {
          if (tag?.tagIcon) {
            return (
              <div
                key={index}
                className="tag-icon-container"
                style={{ display: "flex", gap: 6, alignItems: "center" }}
              >
                <img
                  src={tag?.tagIcon?.fullPath}
                  alt={tag?.name}
                  className="veg-icon"
                />
              </div>
            );
          }
          return null;
        })}
        {hasProteinBreadChoice && (
          <div
            className="protein-tag-container"
            style={{ display: "flex", gap: 0, alignItems: "center" }}
          >
            <img
              src={PROTEIN_BREAD_BADGE_SRC}
              alt="Protein Bread"
              className="egg-nonveg-icon"
            />
          </div>
        )}
      </div>
    );
  };

  // Add button / quantity stepper (only when cart is enabled)
  const renderCartControls = () => {
    if (!isCartEnabled) return null;
    return cartQuantity > 0 ? (
      <div className="quick-qty-controls" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="quick-qty-btn"
          onClick={handleQuickDecrease}
          disabled={isSoldOut}
          aria-label={`Remove one ${product.name} from cart`}
        >
          −
        </button>
        <span className="quick-qty-value" aria-live="polite">{cartQuantity}</span>
        <button
          type="button"
          className="quick-qty-btn"
          onClick={handleQuickAdd}
          disabled={isSoldOut}
          aria-label={`Add one more ${product.name} to cart`}
        >
          +
        </button>
      </div>
    ) : (
      <button
        type="button"
        className="quick-add-btn"
        onClick={handleQuickAdd}
        disabled={isSoldOut}
        aria-label={`Add ${product.name} to cart`}
      >
        ADD
      </button>
    );
  };

  const productName = highlightText && searchTerm
    ? highlightText(product.name, searchTerm)
    : product.name;
  const descriptionNode = cleanDescription && (
    <p ref={descRef} className={`product-description ${!showFullDetails ? "truncate" : ""}`}>
      {highlightText && searchTerm ? highlightText(combinedText, searchTerm) : combinedText}
    </p>
  );

  // ---- Tile variant: photo-forward vertical card for the kiosk grid ----
  if (variant === "tile") {
    return (
      <div
        className={`product-card tile ${isSoldOut ? 'sold-out' : ''}`}
        onClick={handleCardClick}
      >
        <div className="tile-media">
          <img src={imageSrc} alt={product.name} className="product-image" loading="lazy" decoding="async" />
          <div className="tile-tags">{renderTags()}</div>
          {isSoldOut && <div className="sold-out-badge-floating">Sold Out</div>}
          {isSoldOut && <div className="sold-out-overlay"></div>}
        </div>
        <div className="tile-body">
          <h4 className="product-name">{productName}</h4>
          {descriptionNode}
          <div className="tile-foot">
            <h6 className="product-price">₹{Math.round(product.price)}</h6>
            {renderCartControls()}
          </div>
        </div>
      </div>
    );
  }

  // ---- Row variant (default, unchanged list layout) ----
  return (
    <div className={`product-card ${isSoldOut ? 'sold-out' : ''}`} onClick={handleCardClick}>
      {isSoldOut && <div className="sold-out-badge-floating">Sold Out</div>}
      <div className="product-left">
        {renderTags()}

        <h4 className="product-name">
          {productName}
        </h4>
        <h6 className="product-price">₹{Math.round(product.price)}</h6>
        {descriptionNode}
        {(cleanDescription.length > 200) && (
            <p
              className="more-link"
              onClick={(e) => {
                e.stopPropagation();
                toggleDetails();
              }}
            >
              {showFullDetails ? "Show Less" : "More"}
            </p>
          )}
      </div>

      <div className="product-right">
        <img src={imageSrc} alt={product.name} className="product-image" />
        {renderCartControls()}
        {isSoldOut && <div className="sold-out-overlay"></div>}
      </div>
    </div>
  );
}
